<template>
  <div class="reports-page">
    <div class="reports-hero">
      <div>
        <h2>财务报表</h2>
        <p>按时间、账户、分类查看收入支出结构</p>
      </div>
      <el-button type="primary" :loading="loading" @click="loadReport">刷新</el-button>
    </div>

    <el-card class="filter-card" shadow="never">
      <el-form class="filter-form" label-position="top">
        <el-form-item label="日期范围" class="filter-span">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            class="filter-control"
          />
        </el-form-item>
        <el-form-item label="收入/支出">
          <el-select v-model="filters.transaction_type" clearable placeholder="全部" class="filter-control" @change="handleTypeChange">
            <el-option label="收入" value="收入" />
            <el-option label="支出" value="支出" />
          </el-select>
        </el-form-item>
        <el-form-item label="账户类型">
          <el-select v-model="filters.account_type" clearable placeholder="全部账户类型" class="filter-control" @change="handleAccountTypeChange">
            <el-option v-for="type in accountTypes" :key="type" :label="type" :value="type" />
          </el-select>
        </el-form-item>
        <el-form-item label="账户" class="filter-span">
          <AccountSelect v-model="filters.account_id" placeholder="全部账户" class="filter-control" include-hidden />
        </el-form-item>
        <el-form-item label="分类">
          <el-select
            v-model="filters.category_id"
            :disabled="!filters.transaction_type"
            clearable
            filterable
            :placeholder="filters.transaction_type ? '全部分类' : '先选择收入或支出'"
            class="filter-control"
          >
            <el-option v-for="category in categoryOptions" :key="category.id" :label="category.category_name" :value="category.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="操作" class="filter-actions">
          <el-button type="primary" :loading="loading" @click="loadReport">查询</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <div class="summary-grid">
      <el-card class="metric-card income" shadow="never">
        <span>总收入</span>
        <strong>¥{{ formatAmount(report.income_total) }}</strong>
      </el-card>
      <el-card class="metric-card expense" shadow="never">
        <span>总支出</span>
        <strong>¥{{ formatAmount(report.expense_total) }}</strong>
      </el-card>
      <el-card class="metric-card net" shadow="never">
        <span>净收入</span>
        <strong :class="report.net_total >= 0 ? 'income-text' : 'expense-text'">¥{{ formatAmount(report.net_total) }}</strong>
      </el-card>
      <el-card class="metric-card count" shadow="never">
        <span>记录数</span>
        <strong>{{ report.total_count || 0 }}</strong>
      </el-card>
    </div>

    <div class="comparison-card">
      <div class="section-title">
        <h3>收入支出对比</h3>
        <span>收入占比 {{ incomePercent }}%</span>
      </div>
      <div class="comparison-bar">
        <div class="income-bar" :style="{ width: `${incomePercent}%` }"></div>
        <div class="expense-bar" :style="{ width: `${expensePercent}%` }"></div>
      </div>
      <div class="comparison-legend">
        <span><i class="dot income-dot"></i>收入 ¥{{ formatAmount(report.income_total) }}</span>
        <span><i class="dot expense-dot"></i>支出 ¥{{ formatAmount(report.expense_total) }}</span>
      </div>
    </div>

    <div class="charts-grid">
      <el-card class="chart-card" shadow="never">
        <template #header>
          <div class="section-title">
            <h3>分类占比</h3>
            <span>{{ report.by_category.length }} 类</span>
          </div>
        </template>
        <PieChart :items="report.by_category" empty-text="暂无分类数据" />
      </el-card>

      <el-card class="chart-card" shadow="never">
        <template #header>
          <div class="section-title">
            <h3>账户占比</h3>
            <span>{{ report.by_account.length }} 个账户</span>
          </div>
        </template>
        <PieChart :items="report.by_account" empty-text="暂无账户数据" />
      </el-card>
    </div>

    <el-card class="trend-card" shadow="never">
      <template #header>
        <div class="section-title">
          <h3>月度分类对比</h3>
          <span>{{ report.monthly_categories.length }} 个月</span>
        </div>
      </template>
      <div v-if="report.monthly_categories.length" class="month-category-list">
        <div v-for="month in report.monthly_categories" :key="month.month" class="month-category-card">
          <div class="month-category-head">
            <h4>{{ month.month }}</h4>
            <span>
              收入 ¥{{ formatCompact(getCategoryTotal(month.income_categories)) }}
              / 支出 ¥{{ formatCompact(getCategoryTotal(month.expense_categories)) }}
            </span>
          </div>
          <div class="month-category-columns">
            <div class="month-category-column">
              <div class="category-column-title income-text">收入分类</div>
              <div v-if="month.income_categories.length" class="category-bars">
                <div v-for="item in month.income_categories" :key="`income-${month.month}-${item.name}`" class="category-bar-row">
                  <span>{{ item.name }}</span>
                  <div class="category-bar-track">
                    <div class="category-bar income-month" :style="{ width: `${getCategoryWidth(item.amount)}%` }">
                      ¥{{ formatCompact(item.amount) }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="category-empty">无收入</div>
            </div>
            <div class="month-category-column">
              <div class="category-column-title expense-text">支出分类</div>
              <div v-if="month.expense_categories.length" class="category-bars">
                <div v-for="item in month.expense_categories" :key="`expense-${month.month}-${item.name}`" class="category-bar-row">
                  <span>{{ item.name }}</span>
                  <div class="category-bar-track">
                    <div class="category-bar expense-month" :style="{ width: `${getCategoryWidth(item.amount)}%` }">
                      ¥{{ formatCompact(item.amount) }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="category-empty">无支出</div>
            </div>
          </div>
        </div>
      </div>
      <el-empty v-else description="暂无月度数据" />
    </el-card>
  </div>
</template>

<script>
import { computed, defineComponent, h, onMounted, reactive, ref } from 'vue'
import api from '../api'
import dayjs from 'dayjs'
import AccountSelect from '../components/AccountSelect.vue'

const chartColors = ['#2f80ed', '#27ae60', '#f2994a', '#eb5757', '#9b51e0', '#00a3a3', '#f2c94c', '#6fcf97', '#56ccf2', '#bb6bd9', '#828282', '#ff7b54']

const PieChart = defineComponent({
  name: 'PieChart',
  props: {
    items: { type: Array, default: () => [] },
    emptyText: { type: String, default: '暂无数据' }
  },
  setup(props) {
    const total = computed(() => props.items.reduce((sum, item) => sum + Number(item.amount || 0), 0))
    const gradient = computed(() => {
      if (!total.value) return '#eef2f7'
      let cursor = 0
      const stops = props.items.map((item, index) => {
        const amount = Number(item.amount || 0)
        const start = cursor
        const end = cursor + (amount / total.value) * 100
        cursor = end
        const color = chartColors[index % chartColors.length]
        return `${color} ${start}% ${end}%`
      })
      return `conic-gradient(${stops.join(', ')})`
    })

    return () => h('div', { class: 'pie-wrap' }, [
      total.value
        ? h('div', { class: 'pie-chart', style: { background: gradient.value } }, [
            h('div', { class: 'pie-center' }, [h('strong', `¥${Number(total.value).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`), h('span', '合计')])
          ])
        : h('div', { class: 'pie-empty' }, props.emptyText),
      h('div', { class: 'pie-legend' }, props.items.map((item, index) => {
        const percent = total.value ? ((Number(item.amount || 0) / total.value) * 100).toFixed(1) : '0.0'
        return h('div', { class: 'legend-row', key: item.name }, [
          h('span', { class: 'legend-name' }, [h('i', { style: { backgroundColor: chartColors[index % chartColors.length] } }), item.name]),
          h('strong', `¥${Number(item.amount || 0).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`),
          h('em', `${percent}%`)
        ])
      }))
    ])
  }
})

export default {
  name: 'Reports',
  components: { PieChart, AccountSelect },
  setup() {
    const loading = ref(false)
    const dateRange = ref([dayjs().startOf('year').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')])
    const accounts = ref([])
    const expenseCategories = ref([])
    const incomeCategories = ref([])
    const filters = reactive({
      transaction_type: '',
      account_type: '',
      account_id: null,
      category_id: null
    })
    const report = reactive({
      total_count: 0,
      income_total: 0,
      expense_total: 0,
      net_total: 0,
      by_type: [],
      by_account: [],
      by_category: [],
      monthly: [],
      monthly_categories: []
    })

    const accountTypes = computed(() => [...new Set(accounts.value.map(account => account.account_type).filter(Boolean))])
    const filteredAccounts = computed(() => {
      if (!filters.account_type) return accounts.value
      return accounts.value.filter(account => account.account_type === filters.account_type)
    })
    const categoryOptions = computed(() => {
      if (filters.transaction_type === '收入') return incomeCategories.value
      if (filters.transaction_type === '支出') return expenseCategories.value
      return []
    })
    const comparisonTotal = computed(() => Number(report.income_total || 0) + Number(report.expense_total || 0))
    const incomePercent = computed(() => comparisonTotal.value ? Math.round((Number(report.income_total || 0) / comparisonTotal.value) * 100) : 0)
    const expensePercent = computed(() => comparisonTotal.value ? 100 - incomePercent.value : 0)
    const maxCategoryAmount = computed(() => {
      return Math.max(
        1,
        ...report.monthly_categories.flatMap(month => [
          ...month.income_categories.map(item => Number(item.amount || 0)),
          ...month.expense_categories.map(item => Number(item.amount || 0))
        ])
      )
    })

    const loadOptions = async () => {
      const [accountData, expenseData, incomeData] = await Promise.all([
        api.getAccounts({ include_hidden: false }),
        api.getExpenseMajorCategories(),
        api.getIncomeMajorCategories()
      ])
      accounts.value = accountData || []
      expenseCategories.value = expenseData || []
      incomeCategories.value = incomeData || []
    }

    const buildParams = () => {
      const params = {}
      if (dateRange.value?.length === 2) {
        params.start_date = dateRange.value[0]
        params.end_date = dateRange.value[1]
      }
      if (filters.transaction_type) params.transaction_type = filters.transaction_type
      if (filters.account_type) params.account_type = filters.account_type
      if (filters.account_id) params.account_id = filters.account_id
      if (filters.category_id) params.category_id = filters.category_id
      return params
    }

    const loadReport = async () => {
      loading.value = true
      try {
        const data = await api.getReportSummary(buildParams())
        Object.assign(report, {
          total_count: data.total_count || 0,
          income_total: Number(data.income_total || 0),
          expense_total: Number(data.expense_total || 0),
          net_total: Number(data.net_total || 0),
          by_type: data.by_type || [],
          by_account: data.by_account || [],
          by_category: data.by_category || [],
          monthly: data.monthly || [],
          monthly_categories: data.monthly_categories || []
        })
      } finally {
        loading.value = false
      }
    }

    const handleTypeChange = () => {
      filters.category_id = null
    }

    const handleAccountTypeChange = () => {
      if (filters.account_id && !filteredAccounts.value.some(account => account.id === filters.account_id)) {
        filters.account_id = null
      }
    }

    const resetFilters = () => {
      dateRange.value = [dayjs().startOf('year').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')]
      filters.transaction_type = ''
      filters.account_type = ''
      filters.account_id = null
      filters.category_id = null
      loadReport()
    }

    const formatAmount = amount => Number(amount || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const formatCompact = amount => Number(amount || 0).toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    const getCategoryWidth = amount => Math.max(8, Math.round((Number(amount || 0) / maxCategoryAmount.value) * 100))
    const getCategoryTotal = items => items.reduce((sum, item) => sum + Number(item.amount || 0), 0)

    onMounted(async () => {
      await loadOptions()
      await loadReport()
    })

    return {
      loading,
      dateRange,
      filters,
      report,
      accountTypes,
      filteredAccounts,
      categoryOptions,
      incomePercent,
      expensePercent,
      loadReport,
      resetFilters,
      handleTypeChange,
      handleAccountTypeChange,
      formatAmount,
      formatCompact,
      getCategoryWidth,
      getCategoryTotal
    }
  }
}
</script>

<style scoped>
.reports-page {
  min-height: 100%;
}

.reports-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.reports-hero h2,
.section-title h3 {
  margin: 0;
  color: #243042;
}

.reports-hero h2 {
  font-size: 26px;
}

.reports-hero p {
  margin: 6px 0 0;
  color: #8a94a6;
}

.filter-card,
.chart-card,
.trend-card,
.comparison-card,
.metric-card {
  border-radius: 16px;
}

.filter-card {
  margin-bottom: 16px;
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 16px;
}

.filter-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.filter-control {
  width: 100%;
}

.filter-actions :deep(.el-form-item__content) {
  display: flex;
  gap: 8px;
}

.filter-actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.metric-card :deep(.el-card__body) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-card span {
  color: #8a94a6;
  font-size: 13px;
}

.metric-card strong {
  color: #243042;
  font-size: 22px;
}

.income-text,
.metric-card.income strong {
  color: #27ae60;
}

.expense-text,
.metric-card.expense strong {
  color: #eb5757;
}

.comparison-card {
  padding: 18px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-title span {
  color: #8a94a6;
  font-size: 13px;
}

.comparison-bar {
  display: flex;
  height: 18px;
  overflow: hidden;
  margin: 16px 0 10px;
  background: #eef2f7;
  border-radius: 999px;
}

.income-bar {
  background: linear-gradient(90deg, #27ae60, #6fcf97);
}

.expense-bar {
  background: linear-gradient(90deg, #f2994a, #eb5757);
}

.comparison-legend {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  color: #606266;
}

.dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  margin-right: 6px;
  border-radius: 50%;
}

.income-dot {
  background: #27ae60;
}

.expense-dot {
  background: #eb5757;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

:deep(.pie-wrap) {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 20px;
  align-items: center;
}

:deep(.pie-chart) {
  position: relative;
  width: 220px;
  height: 220px;
  border-radius: 50%;
}

:deep(.pie-center) {
  position: absolute;
  inset: 54px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px #edf0f5;
}

:deep(.pie-center strong) {
  max-width: 100px;
  overflow: hidden;
  color: #243042;
  font-size: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.pie-center span) {
  color: #8a94a6;
  font-size: 12px;
}

:deep(.pie-empty) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 220px;
  height: 220px;
  color: #8a94a6;
  background: #f5f7fa;
  border-radius: 50%;
}

:deep(.pie-legend) {
  display: grid;
  gap: 10px;
  min-width: 0;
}

:deep(.legend-row) {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 52px;
  gap: 10px;
  align-items: center;
  color: #606266;
}

:deep(.legend-name) {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.legend-name i) {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 50%;
}

:deep(.legend-row strong) {
  color: #243042;
}

:deep(.legend-row em) {
  color: #8a94a6;
  font-style: normal;
  text-align: right;
}

.month-category-list {
  display: grid;
  gap: 16px;
}

.month-category-card {
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #edf0f5;
  border-radius: 14px;
}

.month-category-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.month-category-head h4 {
  margin: 0;
  color: #243042;
  font-size: 16px;
}

.month-category-head span {
  color: #8a94a6;
  font-size: 13px;
  text-align: right;
}

.month-category-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.month-category-column {
  min-width: 0;
}

.category-column-title {
  margin-bottom: 10px;
  font-weight: 700;
}

.category-bars {
  display: grid;
  gap: 10px;
}

.category-bar-row {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}

.category-bar-row > span {
  min-width: 0;
  overflow: hidden;
  color: #606266;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-bar-track {
  min-width: 0;
  overflow: hidden;
  background: #edf2f7;
  border-radius: 999px;
}

.category-bar {
  min-width: 54px;
  height: 24px;
  padding: 0 8px;
  color: #fff;
  font-size: 12px;
  line-height: 24px;
  border-radius: 999px;
  white-space: nowrap;
}

.category-empty {
  color: #b0b8c4;
  font-size: 13px;
}

.income-month {
  background: linear-gradient(90deg, #27ae60, #6fcf97);
}

.expense-month {
  background: linear-gradient(90deg, #f2994a, #eb5757);
}

@media (max-width: 1024px) {
  .filter-form,
  .summary-grid,
  .charts-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  :deep(.pie-wrap) {
    grid-template-columns: 1fr;
    justify-items: center;
  }

  :deep(.pie-legend) {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .reports-hero {
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .reports-hero h2 {
    font-size: 22px;
  }

  .reports-hero p {
    font-size: 13px;
  }

  .filter-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-span,
  .filter-actions {
    grid-column: 1 / -1;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .charts-grid {
    grid-template-columns: 1fr;
  }

  .filter-card :deep(.el-card__body),
  .chart-card :deep(.el-card__body),
  .trend-card :deep(.el-card__body) {
    padding: 14px;
  }

  .filter-actions :deep(.el-form-item__content) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }

  .filter-actions :deep(.el-button) {
    width: 100%;
  }

  .summary-grid {
    gap: 10px;
  }

  .metric-card strong {
    font-size: 20px;
  }

  .comparison-card {
    padding: 14px;
  }

  .section-title {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  :deep(.pie-chart),
  :deep(.pie-empty) {
    width: 190px;
    height: 190px;
  }

  :deep(.pie-center) {
    inset: 46px;
  }

  :deep(.legend-row) {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  :deep(.legend-row em) {
    display: none;
  }

  .month-category-card {
    padding: 12px;
  }

  .month-category-head {
    flex-direction: column;
    gap: 4px;
  }

  .month-category-head span {
    text-align: left;
  }

  .month-category-columns {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .category-bar-row {
    grid-template-columns: 74px minmax(0, 1fr);
  }
}
</style>
